import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import type { NextStepCardArchiveReason, NextStepCardSnoozeOption } from '@/lib/nextStepCards/dispositionOptions'
import { registerTransientlyHiddenNextStepCardKey } from '@/lib/nextStepCards/transientlyHiddenRegistry'

export type NextStepCardKind =
    | 'video_visit_follow_up'
    | 'lab_order'
    | 'questionnaire'
    | 'imaging_order'
    | 'referral_order'
    | 'in_person_visit_order'
    | 'prescription_order'
    | 'medication_check_in'

export interface NextStepCardAssignment {
    care_plan_id?: string | null
    care_plan_date: string | null
    provider_id?: string | null
    provider_name: string | null
    provider_short_name: string | null
    appointment_id: string | null
    appointment_kind: 'video_visit' | 'chat_visit' | null
}

/** Card envelope; each renderer validates its kind-specific `details` payload. */
export interface NextStepCard<Details = Record<string, unknown>> {
    key: string
    kind: NextStepCardKind
    /** Opaque server-issued id for snooze and dismiss mutations. */
    action_id?: string | null
    deadline: string | null
    assigned_by: NextStepCardAssignment | null
    patient_rationale?: string | null
    details: Details
    created_at: string | null
}

export type NextStepEmptyState = 'needs_first_visit' | 'caught_up'

export interface NextStepCardsResponse {
    cards: NextStepCard[]
    intro_notice_dismissed_at?: string | null
    has_completed_appointment?: boolean | null
}

export interface SnoozeNextStepCardRequest {
    actionId: string
    cardKey: string
    option: NextStepCardSnoozeOption
}

export interface DismissNextStepCardRequest {
    actionId: string
    cardKey: string
    reasons: NextStepCardArchiveReason[]
}

/** Accepts only a boolean completed-visit flag from the Next Steps API. */
export function isKnownCompletedAppointment(value: unknown): value is boolean {
    return value === true || value === false
}

/** Maps the completed-visit flag onto the two Next Steps empty views. */
export function nextStepEmptyStateFromCompletedAppointment(value: unknown): NextStepEmptyState | null {
    if (!isKnownCompletedAppointment(value)) return null
    return value ? 'caught_up' : 'needs_first_visit'
}

/** Rejects malformed envelopes before kind-specific parsing. */
export function isRenderableNextStepCard(card: unknown): card is NextStepCard {
    if (typeof card !== 'object' || card === null) return false

    const candidate = card as Partial<NextStepCard>
    return (
        typeof candidate.key === 'string' &&
        candidate.key.length > 0 &&
        typeof candidate.kind === 'string' &&
        typeof candidate.details === 'object' &&
        candidate.details !== null
    )
}

function normalizeNextStepCard(card: unknown): NextStepCard | null {
    if (!isRenderableNextStepCard(card)) return null

    const rawCard = card as NextStepCard
    return {
        ...rawCard,
        action_id: typeof rawCard.action_id === 'string' ? rawCard.action_id : null,
    }
}

function removeNextStepCardFromCache(draft: NextStepCardsResponse, actionId: string, cardKey: string): void {
    draft.cards = draft.cards.filter((card) => card.action_id !== actionId && card.key !== cardKey)
}

export const nextStepCardsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getNextStepCards: builder.query<NextStepCardsResponse, void>({
            query: () => apiEndpoints.dashboard.getNextStepCards,
            transformResponse: (
                response:
                    | { cards?: unknown[]; intro_notice_dismissed_at?: unknown; has_completed_appointment?: unknown }
                    | undefined,
            ): NextStepCardsResponse => ({
                cards: (response?.cards ?? [])
                    .map(normalizeNextStepCard)
                    .filter((card): card is NextStepCard => card !== null),
                intro_notice_dismissed_at:
                    typeof response?.intro_notice_dismissed_at === 'string' ? response.intro_notice_dismissed_at : null,
                has_completed_appointment: isKnownCompletedAppointment(response?.has_completed_appointment)
                    ? response.has_completed_appointment
                    : null,
            }),
            providesTags: ['NextStepCards'],
        }),
        dismissNextStepsIntro: builder.mutation<{ intro_notice_dismissed_at: string }, void>({
            query: () => ({
                url: apiEndpoints.dashboard.dismissNextStepsIntro,
                method: 'POST',
            }),
            invalidatesTags: ['NextStepCards'],
        }),
        snoozeNextStepCard: builder.mutation<{ card_removed: boolean }, SnoozeNextStepCardRequest>({
            query: ({ actionId, option }) => ({
                url: apiEndpoints.dashboard.snoozeNextStepCard,
                method: 'POST',
                body: { action_id: actionId, option },
            }),
            invalidatesTags: ['NextStepCards'],
            async onQueryStarted({ actionId, cardKey }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    registerTransientlyHiddenNextStepCardKey(cardKey)
                    dispatch(
                        nextStepCardsApi.util.updateQueryData('getNextStepCards', undefined, (draft) => {
                            removeNextStepCardFromCache(draft, actionId, cardKey)
                        }),
                    )
                } catch {
                    // Leave the card and dialog state unchanged on failure.
                }
            },
        }),
        dismissNextStepCard: builder.mutation<{ card_removed: boolean }, DismissNextStepCardRequest>({
            query: ({ actionId, reasons }) => ({
                url: apiEndpoints.dashboard.dismissNextStepCard,
                method: 'POST',
                body: { action_id: actionId, reasons },
            }),
            invalidatesTags: ['NextStepCards'],
            async onQueryStarted({ actionId, cardKey }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    // Hide/remove only after success so the nested archive dialog stays mounted
                    // while the request is pending and can keep selected reasons on failure.
                    registerTransientlyHiddenNextStepCardKey(cardKey)
                    dispatch(
                        nextStepCardsApi.util.updateQueryData('getNextStepCards', undefined, (draft) => {
                            removeNextStepCardFromCache(draft, actionId, cardKey)
                        }),
                    )
                } catch {
                    // Leave the card and dialog state unchanged on failure.
                }
            },
        }),
    }),
    overrideExisting: false,
})

export const {
    useDismissNextStepCardMutation,
    useDismissNextStepsIntroMutation,
    useGetNextStepCardsQuery,
    useSnoozeNextStepCardMutation,
} = nextStepCardsApi
